import { useRef, useState } from "react";
import { adminUploadFile } from "@/lib/api";
import { FileText, Loader2, X, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { normalizeSrc } from "@/lib/images";

export default function PdfUploader({ value, onChange, label = "PDF file", testid = "pdf" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) { toast.error("Please choose a PDF file."); return; }
    if (file.size > 60 * 1024 * 1024) { toast.error("PDF too large (max 60 MB)."); return; }
    setBusy(true);
    try {
      const res = await adminUploadFile(file);
      onChange(normalizeSrc(res.url));
      toast("PDF uploaded");
    } catch (err) {
      toast.error("Upload failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">{label}</label>
      <div className="flex items-start gap-4">
        <div className="flex h-16 w-14 shrink-0 items-center justify-center overflow-hidden border border-line bg-parchment text-ink-3">
          <FileText className={`h-6 w-6 ${value ? "text-saffron" : ""}`} />
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${testid}-upload-btn`} className="inline-flex items-center gap-2 border border-line px-3 py-2 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              {busy ? "Uploading…" : value ? "Replace PDF" : "Upload PDF"}
            </button>
            {value && (
              <a href={normalizeSrc(value)} target="_blank" rel="noreferrer" className="text-xs text-saffron underline">Open</a>
            )}
            {value && (
              <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1 text-xs text-ink-3 hover:text-[var(--saffron-dark)]">
                <X className="h-3.5 w-3.5" /> Remove
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <LinkIcon className="h-3.5 w-3.5 shrink-0 text-ink-3" />
            <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="or paste a PDF URL" data-testid={`${testid}-url-input`} className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
          </div>
          <input ref={inputRef} type="file" accept="application/pdf,.pdf" onChange={onFile} className="hidden" data-testid={`${testid}-file-input`} />
        </div>
      </div>
    </div>
  );
}
